import type { CustomAxiosRequestConfig } from 'axios-auth-refresh/dist/utils';
import type { EnumType } from 'src/core/types/enum';
import { PlanType } from 'src/modules/usage/types/billing';
import { SupplierType } from 'src/modules/userAccess/types';
import type { AccountType } from 'src/modules/userAccess/customers/types/customer';

/**
 * Response type for the consumption read API.
 */
export type ConsumptionReadResponse = {
  plan: PlanType;
  usage_consumption: number;
  over_consumption_limit: boolean;
};
/**
 * Configuration object for custom Axios requests.
 */
export const customAxiosRequestConfig: CustomAxiosRequestConfig = {
  skipAuthRefresh: true,
};

type Customer = {
  id: number;
  company_name: string;
  created_at: string;
  status: string | null;
  mfa_configuration: string | null;
  updated_at: string;
  deactivated_at: string | null;
  commodities: string[];
  custom_consumption_limit?: number | null;
  add_on?: number;
  plan?: PlanType;
  enterprise_bundle?: boolean;
  crop_extraction_enabled?: boolean; // whether this customer may use crop extraction; nested in /user/read/self
  account_type?: AccountType;
  test_profiles?: string[];
};

/**
 * One test profile + allowed origin countries from user/read/self (test_profiles_access array item).
 */
export type UserTestProfileAccess = {
  profile: EnumType;
  countries: EnumType[];
};

/**
 * Represents the server response for a user.
 */
export type UserServerResponse = {
  id: number;
  first_name: string;
  avatar: string;
  email: string;
  last_name: string;
  permissions: string[];
  access_rights: any;
  test_profiles_access?: UserTestProfileAccess[];
  plan: PlanType;
  usage_consumption: number;
  over_consumption_limit: boolean;
  impersonated_by?: { first_name: string; last_name: string; id: number };
  role_ids: number[];
  roles: string[];
  customer?: Customer;
  supplier?: SupplierType;
  supplier_access?: boolean;
  reclassification_access?: boolean;
  is_eudr_integration_enabled?: boolean;
  is_data_preparation_enabled?: boolean;
};

/**
 * Represents the server response for a login request.
 */
export type LoginServerResponse = {
  access_token: string;
  refresh_token: string;
};

/**
 * Represents the response object for generating an API token.
 */
export type GenerateApiTokenResponse = {
  token: string;
};

/**
 * Represents the response object for getting API tokens.
 */
export type GetApiTokensReponse = {
  token: string;
  valid_till_date: string;
}[];

/**
 * Represents a login request.
 */
export type LoginRequest = {
  email: string;
  password: string;
  remember_me: boolean;
};

/**
 * Represents a refresh request object.
 */
export type RefreshRequest = {
  refresh_token: string;
};

/**
 * Represents a request to generate an API token.
 */
export type RequestGenerateApiToken = {
  days: number;
};

/**
 * Represents the type for user settings.
 */
export type UserSettingsType = {
  formats: SettingsFormatsType;
};

/**
 * Represents the settings formats type.
 */
export type SettingsFormatsType = {
  date_full: string;
  date_short: string;
};

/**
 * Represents the request object for confirming an invite.
 */
export type InviteConfirmRequest = {
  password: string;
  token: string;
};

/**
 * Represents the response object for confirming an invite.
 */
export type InviteConfirmResponse = {
  access_token: string;
  refresh_token: string;
};

/**
 * Represents a request to update a forgotten password.
 */
export type ForgotPasswordUpdateRequest = {
  password: string;
  token: string;
};

/**
 * Represents the response object for the forgot password update operation.
 */
export type ForgotPasswordUpdateResponse = {
  access_token: string;
  refresh_token: string;
};
